import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { DriverCreate, type Driver } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { sendInvite } from '../lib/invite';
import { Dialog, useToast } from '../components/ui';
import { useDirectory } from '../lib/directory';
import { useMe } from '../lib/me';
import { putDriver } from '../lib/fs';
import { useStepUp } from '../components/StepUp';

export const COLORS = ['#0b6bcb', '#7a4ec2', '#d17a12', '#138a8a', '#b0457a', '#2e7d32', '#c62828', '#5d4037'];
export const TYPE = { workspace: '会社', personal: '個人 Gmail', apple: 'Apple ID', email: 'メールのリンク' } as const;
const STATUS = { invited: ['招待済み・未ログイン', 'warn'], pending: ['端末の承認待ち', 'warn'], active: ['利用中', 'ok'], retired: ['退職', ''] } as const;

export function DriversPage() {
  const toast = useToast();
  const [rows, setRows] = useState<Driver[] | null>(null);
  const [open, setOpen] = useState(false);
  const dir = useDirectory();
  const [showRetired, setShowRetired] = useState(false);
  // 「退職者も表示」を切り替えた直後に前の読み込みが遅れて届いても、最後に頼んだ結果だけを出す
  const seq = useRef(0);
  const load = () => { const n = ++seq.current; return api.get<{ drivers: Driver[] }>(`/v1/office/drivers${showRetired ? '?all=1' : ''}`).then((r) => { if (n === seq.current) setRows(r.drivers); }); };
  useEffect(() => { void load(); }, [showRetired]);
  return (
    <div className="page">
      <div className="head" style={{ justifyContent: 'space-between' }}>
        <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> 退職者も表示</label>
        <button className="btn pri" onClick={() => setOpen(true)}>＋ ドライバーを追加</button>
      </div>
      <div className="panel">
        <table>
          <thead><tr><th>ドライバー</th><th>ログインするアカウント</th><th>状態</th><th>地図の色</th></tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={4} className="muted">読み込み中…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={4} className="muted">まだドライバーがいません</td></tr>
              : rows.map((d) => { const p = dir.drivers.get(d.id); return (
                <tr key={d.id}><td><Link to={`/drivers/${d.id}`} style={{ color: 'inherit' }}><b>{p?.name ?? '—'}</b></Link><small>{p?.phone ?? ''}</small></td><td>{p?.email ?? '—'}<small>{TYPE[d.accountType]}</small></td>
                  <td><span className={`pill ${STATUS[d.status][1]}`}>{STATUS[d.status][0]}</span></td>
                  <td><span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: 5, background: d.color }} /></td></tr>
              ); })}
          </tbody>
        </table>
      </div>
      {open && <AddDriver onClose={() => setOpen(false)} onDone={(email) => { setOpen(false); load(); toast(`${email} を追加しました`); }} />}
    </div>
  );
}

function AddDriver({ onClose, onDone }: { onClose: () => void; onDone: (email: string) => void }) {
  const toast = useToast();
  const [f, setF] = useState({ name: '', phone: '', email: '', accountType: 'workspace' as Driver['accountType'], color: COLORS[5]! });
  const [err, setErr] = useState('');
  const me = useMe();
  const dir = useDirectory();
  const { ensureRecent } = useStepUp();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = { ...f, phone: f.phone || undefined };
    const r = DriverCreate.safeParse(body);
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    const { name, phone, email, ...reg } = r.data;
    if ([...dir.drivers.values()].some((d) => d.email === email)) { setErr('このメールアドレスはもう登録されています'); return; }
    if (!(await ensureRecent('ドライバーの登録'))) return;
    try {
      // D1 には ID と種類だけ、名前・電話・メールは Firestore に書く
      const { id } = await api.post<{ id: string }>('/v1/office/drivers', reg);
      await putDriver(id, { officeId: me!.officeId, name, email, phone: phone ?? null, active: true, uid: null });
      await sendInvite('driver', id, toast);
      onDone(email);
    } catch (er) { setErr(er instanceof HttpError ? er.message : '登録できませんでした'); }
  };
  return (
    <Dialog title="ドライバーを追加" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
        <div className="field"><label htmlFor="n">名前（必須）</label><input id="n" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="field"><label htmlFor="p">電話番号</label><input id="p" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="090-0000-0000" /></div>
        <div className="field"><label htmlFor="m">ログインに使うアドレス（必須）</label><input id="m" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div className="field"><label htmlFor="t">アカウントの種類</label>
          <select id="t" value={f.accountType} onChange={(e) => setF({ ...f, accountType: e.target.value as Driver['accountType'] })}>
            {Object.entries(TYPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div className="field"><label>地図の色</label><div style={{ display: 'flex', gap: 6 }}>
          {COLORS.map((c) => <button type="button" key={c} aria-label={c} onClick={() => setF({ ...f, color: c })} style={{ width: 28, height: 28, borderRadius: 8, border: 0, background: c, outline: f.color === c ? '3px solid #000' : 'none', outlineOffset: 2, cursor: 'pointer' }} />)}
        </div></div>
        {err && <p className="err" role="alert">{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn" onClick={onClose}>やめる</button><button className="btn pri">追加する</button></div>
      </form>
    </Dialog>
  );
}
